import { useFormatter, useTranslations } from "use-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  Check,
  Info,
  Lightbulb,
  Pause,
  Play,
  Plus,
  Repeat,
  Search,
  Settings2,
  TrendingDown,
  TrendingUp,
  Undo2,
  Video,
} from "lucide-react";
import { ExerciseVideo } from "@/components/app/video";
import { Stepper } from "@/components/app/stepper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import type { LoggedSet, TrainingData, TrainingItem } from "./model";
import { beep, vibrate } from "@/lib/feedback";
import { roundWeight } from "@liftquest/shared/progression";
import { cn } from "@/lib/utils";

export type NewSet = {
  setIndex: number;
  isWarmup: boolean;
  weight: number | null;
  reps: number | null;
  durationSec: number | null;
  distanceKm: number | null;
};

type Row = { key: string; isWarmup: boolean; index: number; logged: LoggedSet | null };

export function ExerciseStep({
  item,
  isFirst,
  phase,
  week,
  beginner,
  logged,
  library,
  onLog,
  onUndo,
  onSwap,
  swapped,
}: {
  item: TrainingItem;
  isFirst: boolean;
  phase: TrainingData["phase"];
  week: number;
  beginner: boolean;
  logged: LoggedSet[];
  library: TrainingData["library"];
  onLog: (s: NewSet) => Promise<void>;
  onUndo: (setId: string) => void;
  onSwap: (exerciseId: string | null) => Promise<void>;
  swapped: boolean;
}) {
  const t = useTranslations("train");
  const ts = useTranslations("suggestion");
  const tm = useTranslations("muscles");
  const te = useTranslations("exercises");
  const format = useFormatter();
  const [videoOpen, setVideoOpen] = useState(false);
  const [altOpen, setAltOpen] = useState(false);
  const [extraSets, setExtraSets] = useState(0);
  const [skipWarmup, setSkipWarmup] = useState(false);
  const [saving, setSaving] = useState(false);

  const ex = item.exercise;
  const s = item.suggestion;
  const kg = (n: number | null | undefined) => format.number(n ?? 0, { maximumFractionDigits: 2 });

  const working = logged.filter((l) => !l.isWarmup);
  const warmups = logged.filter((l) => l.isWarmup);
  const warmupCount = ex.type === "reps" && !skipWarmup && working.length === 0 ? item.warmupSets : warmups.length;
  const workingCount = Math.max(item.sets + extraSets, working.length);

  const rows: Row[] = [
    ...Array.from({ length: warmupCount }, (_, i) => ({
      key: `w${i}`,
      isWarmup: true,
      index: i,
      logged: warmups[i] ?? null,
    })),
    ...Array.from({ length: workingCount }, (_, i) => ({
      key: `s${i}`,
      isWarmup: false,
      index: i,
      logged: working[i] ?? null,
    })),
  ];
  const active = rows.find((r) => !r.logged) ?? null;
  const allDone = working.length >= item.sets;

  const lastMax = item.last ? Math.max(0, ...item.last.sets.map((x) => x.weight ?? 0)) : 0;
  const plannedWeight = working.at(-1)?.weight ?? s.weight ?? (lastMax || null);

  const target =
    ex.type === "reps"
      ? t("targetReps", { sets: item.sets, min: item.targetMin, max: item.targetMax })
      : ex.type === "time"
        ? t("targetTime", { sets: item.sets, min: item.targetMin, max: item.targetMax })
        : t("targetCardio", { min: item.targetMin, max: item.targetMax });

  const suggestionText = ts(s.kind, {
    delta: kg(Math.abs(s.delta)),
    weight: kg(s.weight),
    target: s.target,
  });
  const SuggestionIcon =
    s.kind === "increase" || s.kind === "add-load" || s.kind === "cardio-up" || s.kind === "time-up"
      ? TrendingUp
      : s.kind === "deload"
        ? TrendingDown
        : s.kind === "first"
          ? Lightbulb
          : Repeat;

  async function submit(set: NewSet) {
    setSaving(true);
    try {
      await onLog(set);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pt-4">
      {/* Title */}
      <div className="flex flex-wrap gap-1.5">
        {ex.muscles.map((m) => (
          <Badge key={m} variant="secondary" className="font-normal">
            {tm.has(m as "abs") ? tm(m as "abs") : m}
          </Badge>
        ))}
      </div>
      <h1 className="mt-2 text-3xl font-black leading-tight tracking-tight text-balance">{ex.name}</h1>
      {swapped && (
        <p className="mt-1 text-sm text-muted-foreground line-through decoration-1">{item.original.name}</p>
      )}
      <p className="mt-1 font-semibold text-primary">
        {target}
        {item.restSec > 0 && <span className="font-normal text-muted-foreground"> · {t("rest", { sec: item.restSec })}</span>}
      </p>

      {/* Quick actions */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="secondary" size="lg" className="h-12 rounded-2xl" onClick={() => setVideoOpen(true)}>
          <Video /> {te("watchVideo")}
        </Button>
        <Button variant="outline" size="lg" className="h-12 rounded-2xl" onClick={() => setAltOpen(true)}>
          <Repeat /> {t("alternative")}
        </Button>
      </div>

      {/* Phase hint */}
      {phase && isFirst && (
        <p className="mt-4 flex gap-2 rounded-2xl bg-muted/60 p-3 text-sm">
          <Info className="mt-0.5 size-4 shrink-0 text-primary" />
          <span>{t("phaseHint", { week, goal: phase.goal })}</span>
        </p>
      )}

      {/* Suggestion */}
      <div
        className={cn(
          "mt-4 flex gap-3 rounded-2xl border p-3.5",
          s.kind === "increase" || s.kind === "add-load"
            ? "border-primary/40 bg-primary/10"
            : s.kind === "deload"
              ? "border-xp/40 bg-xp/10"
              : "bg-card",
        )}
      >
        <SuggestionIcon className="mt-0.5 size-5 shrink-0 text-primary" />
        <div className="min-w-0 text-sm">
          <p className="font-semibold">
            {t("today")}
            {s.weight != null && ex.type === "reps" && (
              <span className="ml-1.5 tabular-nums">
                {kg(s.weight)} kg × {s.target}
              </span>
            )}
          </p>
          <p className="text-muted-foreground">{suggestionText}</p>
          {item.last && (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {t("lastTime")} ({format.relativeTime(new Date(item.last.date), new Date())}):{" "}
              <span className="font-medium text-foreground tabular-nums">
                {item.last.sets
                  .map((x) =>
                    ex.type === "reps"
                      ? `${x.weight ? kg(x.weight) + " kg × " : ""}${x.reps}`
                      : ex.type === "time"
                        ? `${x.durationSec} s`
                        : `${Math.round((x.durationSec ?? 0) / 60)} min`,
                  )
                  .join(" · ")}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Beginner help inline */}
      {beginner && (ex.setup || ex.instructions.length > 0) && (
        <details className="group mt-4 rounded-2xl border bg-card p-3.5" open={!item.last}>
          <summary className="flex cursor-pointer list-none items-center gap-2 font-semibold">
            <Lightbulb className="size-4 text-xp" /> {t("howTo")}
            <span className="ml-auto text-xs text-muted-foreground group-open:hidden">▼</span>
          </summary>
          {ex.setup && (
            <p className="mt-3 flex gap-2 text-sm">
              <Settings2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>{ex.setup}</span>
            </p>
          )}
          <ol className="mt-3 list-decimal space-y-1.5 pl-5 text-sm">
            {ex.instructions.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
          {ex.type === "reps" && <p className="mt-3 text-xs text-muted-foreground">⏱ {t("tempoHint")}</p>}
        </details>
      )}

      {item.notes && <p className="mt-3 text-sm text-muted-foreground">📝 {item.notes}</p>}

      {/* Sets */}
      <ol className="mt-5 space-y-2">
        {rows.map((row) => {
          const isActive = active?.key === row.key;
          const label = row.isWarmup ? `${t("warmupSet")} ${row.index + 1}` : t("set", { n: row.index + 1 });
          if (row.logged) {
            const l = row.logged;
            return (
              <li
                key={row.key}
                className="flex items-center gap-3 rounded-2xl border border-success/30 bg-success/10 px-3.5 py-2.5"
              >
                <span className="flex size-7 items-center justify-center rounded-full bg-success text-background">
                  <Check className="size-4" strokeWidth={3} />
                </span>
                <span className="text-sm text-muted-foreground">{label}</span>
                <span className="ml-auto font-semibold tabular-nums">
                  {ex.type === "reps"
                    ? `${l.weight ? kg(l.weight) + " kg × " : ""}${l.reps}`
                    : ex.type === "time"
                      ? `${l.durationSec} s`
                      : `${Math.round((l.durationSec ?? 0) / 60)} min${l.distanceKm ? ` · ${kg(l.distanceKm)} km` : ""}`}
                </span>
                {l.rir != null && <span className="text-xs text-muted-foreground">RIR {l.rir}</span>}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("undo")}
                  onClick={() => onUndo(l.id)}
                  disabled={l.id.startsWith("tmp-")}
                >
                  <Undo2 />
                </Button>
              </li>
            );
          }
          if (!isActive) {
            return (
              <li
                key={row.key}
                className="flex items-center gap-3 rounded-2xl border border-dashed px-3.5 py-2.5 text-muted-foreground"
              >
                <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                  {row.isWarmup ? "W" : row.index + 1}
                </span>
                <span className="text-sm">{label}</span>
              </li>
            );
          }
          return (
            <li key={row.key} className="rounded-2xl border-2 border-primary bg-card p-3.5 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-semibold">{label}</span>
                {row.isWarmup && (
                  <button type="button" className="text-xs font-medium text-primary" onClick={() => setSkipWarmup(true)}>
                    {t("skipWarmup")}
                  </button>
                )}
              </div>
              {row.isWarmup && <p className="-mt-2 mb-3 text-xs text-muted-foreground">{t("warmupSetHint")}</p>}
              <ActiveSet
                type={ex.type}
                weightStep={ex.weightStep || 2.5}
                initial={initialDraft({
                  type: ex.type,
                  isWarmup: row.isWarmup,
                  prev: (row.isWarmup ? warmups : working).at(-1) ?? null,
                  plannedWeight,
                  target: s.target,
                  weightStep: ex.weightStep || 2.5,
                })}
                targetMax={item.targetMax}
                saving={saving}
                onSubmit={(d) => submit({ ...d, setIndex: row.index, isWarmup: row.isWarmup })}
              />
            </li>
          );
        })}
      </ol>

      {allDone && (
        <div className="mt-4 rounded-2xl bg-primary/10 p-4 text-center font-semibold text-primary">
          {t("allSetsDone")}
        </div>
      )}
      {!active && ex.type !== "cardio" && (
        <Button variant="ghost" className="mt-2 w-full" onClick={() => setExtraSets((n) => n + 1)}>
          <Plus /> {t("plusSet")}
        </Button>
      )}
      {beginner && (
        <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
          <AlertTriangle className="size-3.5" /> {t("pain")}
        </p>
      )}

      {/* Video drawer */}
      <Drawer open={videoOpen} onOpenChange={setVideoOpen}>
        <DrawerContent className="max-h-[92dvh]">
          <DrawerHeader className="text-left">
            <DrawerTitle>{ex.name}</DrawerTitle>
            <DrawerDescription>{target}</DrawerDescription>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 pb-8">
            <ExerciseVideo url={ex.videoUrl} title={ex.name} />
            {ex.setup && (
              <>
                <h3 className="mt-5 font-semibold">{t("setup")}</h3>
                <p className="mt-1 text-sm">{ex.setup}</p>
              </>
            )}
            {ex.instructions.length > 0 && (
              <>
                <h3 className="mt-5 font-semibold">{t("howTo")}</h3>
                <ol className="mt-1 list-decimal space-y-1.5 pl-5 text-sm">
                  {ex.instructions.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      <AlternativeDrawer
        open={altOpen}
        onOpenChange={setAltOpen}
        item={item}
        library={library}
        swapped={swapped}
        onSwap={async (id) => {
          await onSwap(id);
          setAltOpen(false);
        }}
      />
    </div>
  );
}

type Draft = { weight: number | null; reps: number | null; durationSec: number | null; distanceKm: number | null };

function initialDraft({
  type,
  isWarmup,
  prev,
  plannedWeight,
  target,
  weightStep,
}: {
  type: TrainingItem["exercise"]["type"];
  isWarmup: boolean;
  prev: LoggedSet | null;
  plannedWeight: number | null;
  target: number;
  weightStep: number;
}): Draft {
  if (type === "time") return { weight: null, reps: null, durationSec: prev?.durationSec ?? target, distanceKm: null };
  if (type === "cardio") return { weight: null, reps: null, durationSec: target * 60, distanceKm: null };
  if (isWarmup) {
    return {
      weight: prev?.weight ?? (plannedWeight ? roundWeight(plannedWeight * 0.5, weightStep) : null),
      reps: 10,
      durationSec: null,
      distanceKm: null,
    };
  }
  return { weight: prev?.weight ?? plannedWeight, reps: prev?.reps ?? target, durationSec: null, distanceKm: null };
}

function ActiveSet({
  type,
  weightStep,
  initial,
  targetMax,
  saving,
  onSubmit,
}: {
  type: TrainingItem["exercise"]["type"];
  weightStep: number;
  initial: Draft;
  targetMax: number;
  saving: boolean;
  onSubmit: (d: Draft) => void;
}) {
  const t = useTranslations("train");
  const [d, setD] = useState<Draft>(initial);

  return (
    <div className="space-y-3">
      {type === "reps" && (
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label={`${t("weight")} (kg)`}
            value={d.weight}
            step={weightStep}
            decimals={2}
            max={1000}
            onChange={(weight) => setD({ ...d, weight })}
          />
          <Stepper label={t("reps")} value={d.reps} max={200} onChange={(reps) => setD({ ...d, reps })} />
        </div>
      )}
      {type === "time" && (
        <HoldTimer
          target={targetMax}
          value={d.durationSec ?? 0}
          onChange={(durationSec) => setD({ ...d, durationSec })}
        />
      )}
      {type === "cardio" && (
        <div className="grid grid-cols-2 gap-2">
          <Stepper
            label={t("minutes")}
            value={d.durationSec != null ? Math.round(d.durationSec / 60) : null}
            max={600}
            onChange={(m) => setD({ ...d, durationSec: m == null ? null : m * 60 })}
          />
          <Stepper
            label={t("distance")}
            value={d.distanceKm}
            step={0.1}
            decimals={2}
            max={100}
            onChange={(distanceKm) => setD({ ...d, distanceKm })}
          />
        </div>
      )}
      <Button
        size="lg"
        className="h-14 w-full rounded-2xl text-base"
        disabled={
          saving ||
          (type === "reps" && !d.reps) ||
          (type !== "reps" && !d.durationSec)
        }
        onClick={() => onSubmit(d)}
      >
        <Check strokeWidth={3} /> {t("logSet")}
      </Button>
    </div>
  );
}

/** Count-up timer for holds like the plank; the value can also be adjusted by hand. */
function HoldTimer({ target, value, onChange }: { target: number; value: number; onChange: (v: number) => void }) {
  const t = useTranslations("train");
  const [running, setRunning] = useState(false);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!running) return;
    const start = Date.now();
    let notified = false;
    const id = setInterval(() => {
      const sec = Math.floor((Date.now() - start) / 1000);
      onChangeRef.current(sec);
      if (sec >= target && !notified) {
        notified = true;
        beep();
        vibrate();
      }
    }, 250);
    return () => clearInterval(id);
  }, [running, target]);

  const pct = useMemo(() => Math.min(1, value / Math.max(1, target)), [value, target]);
  return (
    <div className="space-y-3">
      <div className="relative mx-auto flex size-40 items-center justify-center">
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90">
          <circle cx="50" cy="50" r="44" className="fill-none stroke-muted" strokeWidth="8" />
          <circle
            cx="50"
            cy="50"
            r="44"
            className="fill-none stroke-primary transition-[stroke-dashoffset]"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={276.5}
            strokeDashoffset={276.5 * (1 - pct)}
          />
        </svg>
        <div className="text-center">
          <p className="text-4xl font-black tabular-nums">{value}</p>
          <p className="text-xs text-muted-foreground">/ {target} s</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button
          variant={running ? "destructive" : "secondary"}
          size="lg"
          className="h-12 rounded-2xl"
          onClick={() => {
            if (!running) onChange(0);
            setRunning((r) => !r);
          }}
        >
          {running ? <Pause /> : <Play />} {running ? t("timerStop") : t("timerStart")}
        </Button>
        <Stepper value={value} step={5} max={3600} size="sm" onChange={(v) => onChange(v ?? 0)} />
      </div>
    </div>
  );
}

function AlternativeDrawer({
  open,
  onOpenChange,
  item,
  library,
  swapped,
  onSwap,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: TrainingItem;
  library: TrainingData["library"];
  swapped: boolean;
  onSwap: (id: string | null) => Promise<void>;
}) {
  const t = useTranslations("train");
  const te = useTranslations("exercises");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const list = library
    .filter((e) => e.id !== item.exercise.id)
    .filter((e) => (q ? e.name.toLowerCase().includes(q.toLowerCase()) : e.type === item.original.type))
    .slice(0, 30);

  async function swap(id: string | null) {
    setBusy(id ?? "orig");
    try {
      await onSwap(id);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[92dvh]">
        <DrawerHeader className="text-left">
          <DrawerTitle>{t("alternativeTitle")}</DrawerTitle>
          {item.alternativeNote && (
            <DrawerDescription className="text-base text-foreground">
              {t("alternativeText")} <strong>{item.alternativeNote}</strong>
            </DrawerDescription>
          )}
        </DrawerHeader>
        <div className="space-y-3 overflow-y-auto px-4 pb-8">
          {swapped && (
            <Button variant="outline" className="w-full" disabled={!!busy} onClick={() => swap(null)}>
              <Undo2 /> {t("resetSwap")}: {item.original.name}
            </Button>
          )}
          {item.alternative && item.alternative.id !== item.exercise.id && (
            <Button className="w-full" disabled={!!busy} onClick={() => swap(item.alternative!.id)}>
              <Repeat /> {item.alternative.name}
            </Button>
          )}
          <p className="pt-2 text-sm font-semibold">{t("swapExercise")}</p>
          <div className="relative">
            <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={te("search")}
              className="h-11 pl-9 text-base"
            />
          </div>
          <ul className="divide-y rounded-2xl border">
            {list.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  disabled={!!busy}
                  className="flex w-full items-center justify-between px-3.5 py-3 text-left text-sm hover:bg-muted/60 disabled:opacity-50"
                  onClick={() => swap(e.id)}
                >
                  {e.name}
                  <Repeat className="size-4 text-muted-foreground" />
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="px-3.5 py-3 text-sm text-muted-foreground">{te("empty")}</li>}
          </ul>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
